import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  InputAdornment,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { MapContainer, Marker, Popup, TileLayer, useMap } from "react-leaflet";
import MarkerClusterGroup from "react-leaflet-cluster";
import "leaflet/dist/leaflet.css";
import { Icon, divIcon, point } from "leaflet";
import "../Offices/MapOffices/Style/map.css";
import { Link, useLocation } from "react-router-dom";
import { message } from "antd";
import { NAV } from "../Navbar/navbarTheme";
import { sectionTitleSpacingSx } from "../sectionHeaderStyles";

const HOME_PAGE_MAX_WIDTH = 1480;
const INTRO_TEXT_MAX_WIDTH = 980;
const MAP_ZOOM = 12;

const pageBrandSx = {
  navy: NAV.navy,
  blue: "#1e5a9e",
  muted: NAV.inkMuted,
  blueLight: "#eef5fb",
  blueBorder: "#c5d9ed",
};

const homePageContentSx = {
  maxWidth: HOME_PAGE_MAX_WIDTH,
  mx: "auto",
  width: "100%",
  boxSizing: "border-box",
};

const headerFont = {
  label: { xs: "0.8rem", sm: "0.875rem", md: "0.95rem" },
  title: { xs: "1.65rem", sm: "1.9rem", md: "2.25rem", lg: "2.5rem" },
  subtitle: { xs: "1rem", sm: "1.1rem", md: "1.125rem" },
  body: { xs: "0.9375rem", md: "1rem" },
};

/** Leaflet needs invalidateSize after the map is in a max-width box */
function MapSizeFix() {
  const map = useMap();
  useEffect(() => {
    const id = window.setTimeout(() => map.invalidateSize(), 150);
    return () => window.clearTimeout(id);
  }, [map]);
  return null;
}

const TrackPage = () => {
  const location = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  const [dataSource, setDataSource] = useState([]);
  const [searchCode, setSearchCode] = useState("");
  const [selectedLocation, setSelectedLocation] = useState(null);
  const mapRef = useRef(null);

  useEffect(() => {
    getApiProducts();
    const interval = setInterval(() => {
      getApiProducts();
    }, 600000);

    return () => clearInterval(interval);
  }, []);

  const getApiProducts = async () => {
    try {
      const productsRes = await fetch(`https://uktomars.co.uk/api/posts`);
      if (!productsRes.ok) {
        throw new Error(`HTTP error! Status: ${productsRes.status}`);
      }
      const productsData = await productsRes.json();
      setDataSource(
        productsData.map((item) => ({
          id: item.id,
          code: item.code,
          gps_id: item.gps_id,
          latitude: item.gps_device?.latitude,
          longitude: item.gps_device?.longitude,
          status: item.status,
        })),
      );
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  const handleSearch = (e) => {
    e?.preventDefault?.();
    const found = dataSource.find((item) => item.code === searchCode);
    if (found) {
      if (found.status === 0 && found.latitude && found.longitude) {
        setSelectedLocation({
          lat: found.latitude,
          lng: found.longitude,
          code: found.code,
          status: found.status,
        });
        scrollToMap();
      } else if (found.status === 1) {
        setSelectedLocation({
          status: found.status,
          code: found.code,
        });
        scrollToMap();
      } else {
        setSelectedLocation(null);
        message.success("Location not found!");
      }
    } else {
      setSelectedLocation(null);
      message.error("Location not found!");
    }
  };

  const scrollToMap = () => {
    mapRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const customIcon = new Icon({
    iconUrl: require("../Offices/MapOffices/Image/marker-icon.png"),
    iconSize: [38, 38],
  });

  const createCustomClusterIcon = (cluster) => {
    return new divIcon({
      html: `<div class="cluster-icon">${cluster.getChildCount()}</div>`,
      className: "custom-marker-cluster",
      iconSize: point(33, 33, true),
    });
  };

  return (
    <Box
      component="main"
      sx={{
        width: "100%",
        bgcolor: "#fff",
        pt: "clamp(96px, 12vh, 140px)",
        pb: { xs: 5, md: 7 },
      }}
    >
      <Box sx={{ width: "100%", px: { xs: 2, sm: 3, md: 4 } }}>
        <Box
          sx={{
            ...homePageContentSx,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            textAlign: "center",
          }}
        >
          <Box
            component="header"
            sx={{ mb: { xs: 3.5, md: 4.5 }, width: "100%" }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 2,
                mb: 2,
              }}
            >
              <Box sx={{ width: 48, height: 2, bgcolor: pageBrandSx.blue }} />
              <Typography
                component="span"
                sx={{
                  color: pageBrandSx.blue,
                  fontWeight: 700,
                  letterSpacing: "0.16em",
                  fontSize: headerFont.label,
                  textTransform: "uppercase",
                  lineHeight: 1.2,
                }}
              >
                Track Your Shipment
              </Typography>
              <Box sx={{ width: 48, height: 2, bgcolor: pageBrandSx.blue }} />
            </Box>

            <Typography
              variant="h1"
              sx={{
                color: pageBrandSx.navy,
                fontSize: headerFont.title,
                fontWeight: 800,
                ...sectionTitleSpacingSx,
                lineHeight: { xs: 1.25, md: 1.2 },
                mb: { xs: 1.5, md: 2 },
                px: { xs: 1, sm: 0 },
              }}
            >
              Shipment Tracking &amp; Delivery Updates
            </Typography>

            <Typography
              sx={{
                color: pageBrandSx.muted,
                maxWidth: INTRO_TEXT_MAX_WIDTH,
                mx: "auto",
                fontSize: headerFont.subtitle,
                lineHeight: { xs: 1.65, md: 1.7 },
                mb: { xs: 1.25, md: 1.5 },
                px: { xs: 1, sm: 0 },
              }}
            >
              Monitor your shipment status with our tracking service, designed
              to help customers stay updated throughout the delivery process.
            </Typography>

            <Typography
              sx={{
                color: pageBrandSx.muted,
                maxWidth: INTRO_TEXT_MAX_WIDTH,
                mx: "auto",
                fontSize: headerFont.body,
                lineHeight: 1.65,
                mb: 2.5,
                px: { xs: 1, sm: 0 },
              }}
            >
              Enter your tracking details below to check the latest shipment
              updates.
            </Typography>

            <Box
              sx={{
                width: 44,
                height: 4,
                bgcolor: pageBrandSx.blue,
                borderRadius: 1,
                mx: "auto",
              }}
            />
          </Box>

          <Paper
            elevation={0}
            component="form"
            onSubmit={handleSearch}
            noValidate
            autoComplete="off"
            sx={{
              p: { xs: 2.5, sm: 3, md: 3.5 },
              borderRadius: 3,
              border: `1px solid ${pageBrandSx.blueBorder}`,
              bgcolor: "#fff",
              boxShadow: "0 8px 28px rgba(15, 40, 71, 0.08)",
              width: "100%",
              maxWidth: INTRO_TEXT_MAX_WIDTH,
              mx: "auto",
              mb: 2,
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "stretch",
                gap: { xs: 1.5, sm: 2 },
                flexDirection: { xs: "column", sm: "row" },
              }}
            >
              <TextField
                id="tracking-number"
                aria-label="Tracking details"
                placeholder="e.g. UKTM-12345"
                variant="outlined"
                value={searchCode}
                onChange={(e) => setSearchCode(e.target.value)}
                fullWidth
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon
                        sx={{ color: pageBrandSx.muted, fontSize: 22 }}
                      />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  flex: 1,
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2,
                    bgcolor: pageBrandSx.blueLight,
                    transition: NAV.transition,
                    "& fieldset": {
                      borderColor: pageBrandSx.blueBorder,
                    },
                    "&:hover fieldset": {
                      borderColor: pageBrandSx.blue,
                    },
                    "&.Mui-focused": {
                      bgcolor: "#fff",
                      boxShadow: NAV.searchFocusLift,
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: pageBrandSx.navy,
                      borderWidth: 2,
                    },
                  },
                }}
              />

              <Button
                type="submit"
                variant="contained"
                disabled={!searchCode.trim()}
                sx={{
                  bgcolor: pageBrandSx.navy,
                  px: 4,
                  py: 1.35,
                  fontWeight: 700,
                  fontSize: "0.95rem",
                  borderRadius: 2,
                  textTransform: "none",
                  boxShadow: "0 4px 14px rgba(15, 40, 71, 0.2)",
                  width: { xs: "100%", sm: "auto" },
                  minWidth: { sm: 160 },
                  alignSelf: { sm: "stretch" },
                  transition: NAV.transition,
                  "&:hover": {
                    bgcolor: pageBrandSx.blue,
                    boxShadow: "0 6px 20px rgba(30, 90, 158, 0.28)",
                  },
                  "&.Mui-disabled": {
                    bgcolor: NAV.line,
                    color: pageBrandSx.muted,
                    boxShadow: "none",
                  },
                }}
              >
                Track shipment
              </Button>
            </Box>
          </Paper>

          {selectedLocation ? (
            <Box
              ref={mapRef}
              sx={{
                width: "100%",
                maxWidth: INTRO_TEXT_MAX_WIDTH,
                mx: "auto",
                mb: { xs: 4, md: 5 },
              }}
            >
              {selectedLocation && selectedLocation.status === 0 && (
                <Paper
                  elevation={0}
                  sx={{
                    width: "100%",
                    overflow: "hidden",
                    borderRadius: 3,
                    border: `1px solid ${NAV.line}`,
                    boxShadow: NAV.shadowMenu,
                  }}
                >
                  <Box
                    sx={{
                      px: 2.5,
                      py: 1.5,
                      bgcolor: NAV.navy,
                      color: NAV.surface,
                    }}
                  >
                    <Typography variant="subtitle2" fontWeight={600}>
                      Shipment location — {selectedLocation.code}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      width: "100%",
                      height: { xs: 360, sm: 420, md: 480 },
                      "& .leaflet-container": {
                        width: "100% !important",
                        height: "100% !important",
                        borderRadius: "0 0 12px 12px",
                      },
                    }}
                  >
                    <MapContainer
                      center={[selectedLocation.lat, selectedLocation.lng]}
                      zoom={MAP_ZOOM}
                      style={{ height: "100%", width: "100%" }}
                    >
                      <MapSizeFix />
                      <TileLayer
                        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                        url="https://tile.openstreetmap.org/{z}/{x}/{y}.png"
                      />
                      <MarkerClusterGroup
                        chunkedLoading
                        iconCreateFunction={createCustomClusterIcon}
                      >
                        <Marker
                          position={[
                            selectedLocation.lat,
                            selectedLocation.lng,
                          ]}
                          icon={customIcon}
                        >
                          <Popup>Code: {selectedLocation.code}</Popup>
                        </Marker>
                      </MarkerClusterGroup>
                    </MapContainer>
                  </Box>
                </Paper>
              )}

              {selectedLocation && selectedLocation.status === 1 && (
                <Paper
                  elevation={0}
                  sx={{
                    width: "100%",
                    maxWidth: INTRO_TEXT_MAX_WIDTH,
                    mx: "auto",
                    textAlign: "center",
                    fontSize: { xs: "1.1rem", sm: "1.25rem" },
                    color: "#1b5e20",
                    fontWeight: 600,
                    bgcolor: "#E8F5E9",
                    py: { xs: 4, sm: 5 },
                    px: 3,
                    borderRadius: 3,
                    border: "2px solid #81c784",
                    boxShadow: NAV.shadowMenu,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 1.5,
                  }}
                >
                  <Box
                    sx={{
                      width: 56,
                      height: 56,
                      borderRadius: "50%",
                      bgcolor: "#c8e6c9",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: "1.75rem",
                    }}
                  >
                    <span role="img" aria-label="success">
                      ✅
                    </span>
                  </Box>
                  <Typography
                    variant="h6"
                    sx={{ color: "#2E7D32", fontWeight: 700 }}
                  >
                    Delivered
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{ color: "#388e3c", maxWidth: 420 }}
                  >
                    Your item ({selectedLocation.code}) has been successfully
                    delivered!
                  </Typography>
                </Paper>
              )}
            </Box>
          ) : null}

          <Box
            sx={{
              width: "100%",
              maxWidth: INTRO_TEXT_MAX_WIDTH,
              mx: "auto",
              mt: { xs: 4, md: 5 },
              textAlign: "left",
            }}
          >
            <Typography
              component="p"
              sx={{
                color: NAV.ink,
                fontSize: headerFont.body,
                lineHeight: 1.75,
                m: 0,
              }}
            >
              <Box component="span" sx={{ fontWeight: 700 }}>
                Can&apos;t find your shipment, or no tracking showing?
              </Box>{" "}
              Contact your account manager or our sales team and we&apos;ll
              provide the latest update directly.{" "}
              <Box
                component={Link}
                to="/ContactUs"
                sx={{
                  color: "#29b6f6",
                  fontWeight: 700,
                  textDecoration: "none",
                  "&:hover": { textDecoration: "underline" },
                }}
              >
                Contact Us
              </Box>
            </Typography>

            <Box
              sx={{
                width: "100%",
                height: 1,
                bgcolor: NAV.line,
                my: { xs: 2.5, md: 3 },
              }}
            />

            <Typography
              component="p"
              sx={{
                color: NAV.ink,
                fontSize: headerFont.body,
                lineHeight: 1.75,
                m: 0,
              }}
            >
              <Box component="span" sx={{ fontWeight: 700, color: "#d32f2f" }}>
                Important Notice
              </Box>{" "}
              Live tracking is available for selected shipments. Availability
              may vary depending on the destination country, shipping route, and
              logistics provider. Please confirm tracking availability with your
              account manager before shipment dispatch.
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default TrackPage;
